import React from 'react';

// Components
import ArrowIcon from '@/views/components/common/ArrowIcon';
import RiskProfileRuleLegendField from '@/views/components/charts/risk-profile-rule-visualizer/RiskProfileRuleLegendField';
import { CollapsedSimpleTableRows } from '@/views/components/tables/CollapsibleSimpleTable';
import { StackedBarData } from '@/views/components/charts/LegacyStackedBar';

// Types
import { RiskProfileRule, RiskProfileRuleType, RiskProfileRuleData } from '../types/risk-profiles';

// Utils
import { DEFAULT_RISK_COLORS, formatRiskProfileTargetMinMax, getRiskProfileRuleTargets, getTargetValueFromMinMax } from './risk-profiles';
import { formatMessage, formatPercentage } from './i18n';

/**
 * Returns the lower and upper bounds for a given risk profile rule.
 *
 * @param {RiskProfileRule} rule - The risk profile rule to get bounds for.
 * @returns {{ lower: string; upper: string }} An object containing the lower and upper bounds as strings.
 *
 * The bounds are determined based on the rule type:
 * - For `RiskProfileRuleType.AssetAllocation`, the bounds are fixed income and equity.
 * - For `RiskProfileRuleType.FundType`, the lower bound is the label of the first target, if available.
 * - For `RiskProfileRuleType.RiskLimit` and `RiskProfileRuleType.AllowedRisk`, the bounds are low and high risk levels.
 */
export const getRiskProfileChartBounds = (rule: RiskProfileRule): { lower: string; upper: string } => {
  switch (rule.ruleType) {
    case RiskProfileRuleType.AssetAllocation: {
      return {
        lower: formatMessage('RiskProfile.Rules.AssetType.FIXED_INCOME'),
        upper: formatMessage('RiskProfile.Rules.AssetType.EQUITY'),
      };
    }
    case RiskProfileRuleType.FundType: {
      const targets = getRiskProfileRuleTargets(rule);

      return {
        lower: targets.length > 0 ? targets[0].label : '',
        upper: '',
      };
    }
    case RiskProfileRuleType.RiskLimit: {
      return {
        lower: formatMessage('RiskProfile.Rules.RiskLevel.Low'),
        upper: formatMessage('RiskProfile.Rules.RiskLevel.High'),
      };
    }
    case RiskProfileRuleType.AllowedRisk: {
      return {
        lower: formatMessage('RiskProfile.Rules.RiskLevel.Low'),
        upper: formatMessage('RiskProfile.Rules.RiskLevel.High'),
      };
    }
    default: {
      return {
        lower: formatMessage('RiskProfile.Rules.RiskLevel.Low'),
        upper: formatMessage('RiskProfile.Rules.RiskLevel.High'),
      };
    }
  }
};

/**
 * Formats the risk profile adjustment value into a JSX element.
 *
 * @param {number} [adjustment] - The adjustment value to format. If the value is positive,
 * it will be displayed with an upward arrow icon. If the value is negative, it will be
 * displayed with a downward arrow icon. If the value is zero or undefined, it returns null.
 *
 * @returns {JSX.Element | null} A JSX element containing the formatted adjustment value
 * with the appropriate icon, or null if the adjustment is zero or undefined.
 */
export const formatRiskProfileAdjustment = (adjustment?: number) => {
  if (!adjustment) {
    return null;
  }

  if (adjustment > 0) {
    return (
      <span className="adjustment">
        <ArrowIcon margin="0 4px 0 0" title={formatMessage('Common.Upside')} type="full-up" useHTMLEntity />
        {formatPercentage(adjustment, 2)}
      </span>
    );
  }

  if (adjustment < 0) {
    return (
      <span className="adjustment">
        <ArrowIcon margin="0 4px 0 0" title={formatMessage('Common.Downside')} type="full-down" useHTMLEntity />
        {formatPercentage(Math.abs(adjustment), 2)}
      </span>
    );
  }

  return null;
};

/**
 * Formats the risk profile table data based on the provided parameters.
 *
 * @param {RiskProfileRule} rule - The risk profile rule object.
 * @param {RiskProfileRuleData[]} ruleData - An array of risk profile rule data objects.
 * @param {React.ReactNode} [targetName=formatMessage('Common.Target')] - The display name for the target column.
 * @param {React.ReactNode} [actualName=formatMessage('Common.Actual')] - The display name for the actual column.
 * @returns {Object} The formatted risk profile table data.
 */
export const formatRiskProfileTableData = (
  rule: RiskProfileRule,
  ruleData: RiskProfileRuleData[],
  targetName: React.ReactNode = formatMessage('Common.Target'),
  actualName: React.ReactNode = formatMessage('Common.Actual'),
  differenceName: React.ReactNode = formatMessage('Common.Difference'),
) => {
  const filteredData = ruleData.filter(d => !d.hidden);
  const legendData = {
    headers: ['', targetName, actualName, differenceName] as React.ReactNode[],
    rows: [] as CollapsedSimpleTableRows,
  };

  if (filteredData.some(data => data.value != null)) {
    const adjustmentCells = filteredData.map(data => formatRiskProfileAdjustment(data.adjustment));

    filteredData.forEach((data, index) => {
      legendData.rows.push({
        elements: [
          <RiskProfileRuleLegendField key={data.key} data={data} rule={rule} className="field-justify-start" showTooltip />,
          formatRiskProfileTargetMinMax(data.target.min, data.target.max),
          data.value != null && formatPercentage(data.value, 2),
          adjustmentCells[index],
        ] as React.ReactNode[],
        key: data.subAllocations ? data.key : undefined,
      });

      if (data.subAllocations && data.subAllocations.length > 0) {
        data.subAllocations.forEach(subAllocation => {
          legendData.rows.push({
            className: `sub-allocations sub-allocations-${subAllocation.key.toLowerCase()}`,
            elements: [
              <RiskProfileRuleLegendField
                key={`${data.key}-${subAllocation.key}`}
                data={subAllocation}
                rule={rule}
                className="field-justify-start"
                showTooltip
                showLegendColor={false}
              />,
              null,
              subAllocation.value != null && formatPercentage(subAllocation.value, 2),
              null,
            ] as React.ReactNode[],
            parentKey: data.key,
          });
        });
      }
    });
  }

  return legendData;
};

/**
 * Formats the risk profile chart data for visualization.
 *
 * @param {RiskProfileRule} rule - The risk profile rule to be visualized.
 * @param {RiskProfileRuleData[]} ruleData - The data associated with the risk profile rule.
 * @param {boolean} [singleItemLayout] - Optional flag to determine if the layout is for a single item.
 * @param {React.ReactNode} [targetLabel=formatMessage('Common.Target')] - Optional label for the target data.
 * @param {React.ReactNode} [actualLabel=formatMessage('Common.Actual')] - Optional label for the actual data.
 * @returns {{ targetData: StackedBarData, actualData?: StackedBarData }} The formatted chart data including target and actual data.
 */
export const formatRiskProfileChartData = (
  rule: RiskProfileRule,
  ruleData: RiskProfileRuleData[],
  singleItemLayout?: boolean,
  targetLabel: React.ReactNode = formatMessage('Common.Target'),
  actualLabel: React.ReactNode = formatMessage('Common.Actual'),
) => {
  const targetData: StackedBarData = {
    slices: ruleData.map(data => ({
      value: getTargetValueFromMinMax(data.target.min, data.target.max),
      color: DEFAULT_RISK_COLORS[rule.ruleType][data.key],
    })),
    bounds: !singleItemLayout
      ? getRiskProfileChartBounds(rule)
      : {
          lower: targetLabel,
          upper: '',
        },
  };

  const hasActual = ruleData.some(data => data.value != null);
  const actualData: StackedBarData | undefined = !hasActual
    ? undefined
    : {
        slices: ruleData.map(data => ({
          value: data.value || 0,
          color: DEFAULT_RISK_COLORS[rule.ruleType][data.key],
        })),
        bounds: !singleItemLayout
          ? undefined
          : {
              lower: actualLabel,
              upper: '',
            },
      };

  return {
    targetData,
    actualData,
  };
};
